import { CheckCircle, AlertCircle, AlertTriangle, XCircle, Shuffle, TrendingUp } from 'lucide-react'
import { Button } from '../ui/Button'
import { cn } from '../../lib/utils'
import type { IdentifiedProperty } from './IdentificationPropertyRow'

export type IdRule = '3-property' | '200-percent' | '95-percent' | ''

interface IdRuleComplianceBarProps {
  properties: IdentifiedProperty[]
  idRule: IdRule
  relinquishedFMV: number | null
  disabled: boolean
  onDistributeEvenly: () => void
  onAddProperty?: () => void
  confirmedFMV?: number | null
  totalAllocated?: number
}

type ComplianceStatus = 'no-rule' | 'no-properties' | 'compliant' | 'approaching' | 'exceeded'

interface ComplianceResult {
  status: ComplianceStatus
  totalLabel: string
  targetLabel: string
  badgeText: string
  canAddMore: boolean
  blockReason?: string
}

function formatDollars(value: number): string {
  return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

function computeCompliance(
  properties: IdentifiedProperty[],
  idRule: IdRule,
  relinquishedFMV: number | null
): ComplianceResult {
  if (!idRule) {
    return {
      status: 'no-rule',
      totalLabel: '0 properties identified',
      targetLabel: 'Select an ID rule to see compliance',
      badgeText: 'Pick a rule',
      canAddMore: true,
    }
  }

  const totalFMV = properties.reduce((sum, p) => sum + (p.estimatedFMV ?? 0), 0)
  const propertyCount = properties.filter((p) => p.address.trim()).length
  const basePrice = relinquishedFMV ?? 0

  if (propertyCount === 0) {
    return {
      status: 'no-properties',
      totalLabel: '0 properties identified',
      targetLabel: idRule === '3-property'
        ? 'Up to 3 properties — no FMV cap'
        : idRule === '200-percent'
        ? basePrice > 0
          ? `Cap: ${formatDollars(basePrice * 2)} (200% of ${formatDollars(basePrice)} relinquished)`
          : 'Set relinquished sale price to see cap'
        : 'Add properties to see 95% acquisition target',
      badgeText: 'Add property',
      canAddMore: true,
    }
  }

  if (idRule === '3-property') {
    const exceeded = propertyCount > 3
    const approaching = propertyCount === 3
    const status: ComplianceStatus = exceeded ? 'exceeded' : approaching ? 'approaching' : 'compliant'
    return {
      status,
      totalLabel: `${propertyCount} of 3 properties identified`,
      targetLabel: 'Up to 3 properties — no FMV cap',
      badgeText: exceeded
        ? 'Exceeds rule — fix before submitting'
        : approaching
        ? 'Approaching limit'
        : 'Within IRS rule',
      canAddMore: propertyCount < 3,
      blockReason: exceeded ? 'Exceeds 3-Property Rule: remove properties to continue.' : undefined,
    }
  }

  if (idRule === '200-percent') {
    const cap = basePrice * 2
    const ratio = cap > 0 ? totalFMV / cap : 0
    const exceeded = cap > 0 && totalFMV > cap
    const approaching = !exceeded && ratio >= 0.8
    const status: ComplianceStatus = exceeded ? 'exceeded' : approaching ? 'approaching' : 'compliant'
    return {
      status,
      totalLabel: formatDollars(totalFMV) + ' identified',
      targetLabel: basePrice > 0
        ? `Cap: ${formatDollars(cap)} (200% of ${formatDollars(basePrice)} relinquished)`
        : 'Set relinquished sale price to see cap',
      badgeText: exceeded
        ? 'Exceeds rule — fix before submitting'
        : approaching
        ? 'Approaching limit'
        : 'Within IRS rule',
      canAddMore: !exceeded,
      blockReason: exceeded ? `Total FMV exceeds 200% cap of ${formatDollars(cap)}.` : undefined,
    }
  }

  if (idRule === '95-percent') {
    const required = totalFMV * 0.95
    return {
      status: 'compliant',
      totalLabel: formatDollars(totalFMV) + ' identified',
      targetLabel: totalFMV > 0
        ? `Acquire ≥ 95% (${formatDollars(Math.ceil(required))})`
        : 'Add properties to see 95% acquisition target',
      badgeText: 'Within IRS rule',
      canAddMore: true,
    }
  }

  return {
    status: 'compliant',
    totalLabel: formatDollars(totalFMV) + ' identified',
    targetLabel: 'Select an ID rule to see compliance',
    badgeText: 'Select a rule',
    canAddMore: true,
  }
}

const BADGE_STYLES: Record<ComplianceStatus, string> = {
  'no-rule':
    'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700',
  'no-properties':
    'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700',
  compliant:
    'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700',
  approaching:
    'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700',
  exceeded:
    'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700',
}

const BADGE_ICON: Record<ComplianceStatus, typeof CheckCircle> = {
  'no-rule': AlertCircle,
  'no-properties': AlertCircle,
  compliant: CheckCircle,
  approaching: AlertTriangle,
  exceeded: XCircle,
}

const CONTAINER_STYLES: Record<ComplianceStatus, string> = {
  'no-rule':
    'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
  'no-properties':
    'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
  compliant:
    'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20',
  approaching:
    'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
  exceeded:
    'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
}

const LABEL_STYLES: Record<ComplianceStatus, string> = {
  'no-rule': 'text-amber-800 dark:text-amber-200',
  'no-properties': 'text-amber-800 dark:text-amber-200',
  compliant: 'text-green-800 dark:text-green-200',
  approaching: 'text-amber-800 dark:text-amber-200',
  exceeded: 'text-red-800 dark:text-red-200',
}

const ICON_STYLES: Record<ComplianceStatus, string> = {
  'no-rule': 'text-amber-600 dark:text-amber-400',
  'no-properties': 'text-amber-600 dark:text-amber-400',
  compliant: 'text-green-600 dark:text-green-400',
  approaching: 'text-amber-600 dark:text-amber-400',
  exceeded: 'text-red-600 dark:text-red-400',
}

const BUTTON_STYLES: Record<ComplianceStatus, string> = {
  'no-rule':
    'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30',
  'no-properties':
    'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30',
  compliant:
    'border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30',
  approaching:
    'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30',
  exceeded:
    'border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30',
}

const REINVESTMENT_BAR_FILL: Record<'below' | 'met' | 'over', string> = {
  below: 'bg-amber-500 dark:bg-amber-400',
  met: 'bg-green-500 dark:bg-green-400',
  over: 'bg-green-500 dark:bg-green-400',
}

function computeReinvestmentPercent(
  allocated: number,
  threshold: number | null
): number | null {
  if (!threshold || threshold <= 0) return null
  return Math.round((allocated / threshold) * 100)
}

export function IdRuleComplianceBar({
  properties,
  idRule,
  relinquishedFMV,
  disabled,
  onDistributeEvenly,
  confirmedFMV,
  totalAllocated = 0,
}: IdRuleComplianceBarProps) {
  const result = computeCompliance(properties, idRule, relinquishedFMV)
  const { status } = result
  const BadgeIcon = BADGE_ICON[status]

  const reinvestmentPercent = computeReinvestmentPercent(totalAllocated, relinquishedFMV)
  const displayedBarPercent = reinvestmentPercent !== null ? Math.min(reinvestmentPercent, 100) : 0
  const reinvestmentFillKey: 'below' | 'met' | 'over' =
    reinvestmentPercent === null || reinvestmentPercent < 100
      ? 'below'
      : reinvestmentPercent > 100
      ? 'over'
      : 'met'

  const fmvDisplay =
    confirmedFMV != null && confirmedFMV > 0
      ? formatDollars(confirmedFMV)
      : relinquishedFMV != null && relinquishedFMV > 0
      ? formatDollars(relinquishedFMV)
      : null

  return (
    <div
      className={cn('rounded-lg border px-4 py-3 space-y-2', CONTAINER_STYLES[status])}
      role="status"
      aria-label="IRS rule compliance summary"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <BadgeIcon
            className={cn('h-4 w-4 flex-shrink-0 mt-0.5', ICON_STYLES[status])}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className={cn('text-sm font-bold', LABEL_STYLES[status])}>
              {result.totalLabel}
            </p>
            <p className={cn('text-xs mt-0.5', LABEL_STYLES[status])}>
              {result.targetLabel}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
              BADGE_STYLES[status]
            )}
            aria-live="polite"
          >
            <BadgeIcon className="h-3 w-3" aria-hidden="true" />
            {result.badgeText}
          </span>

          {!disabled && properties.length > 1 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDistributeEvenly}
              aria-label="Distribute sale price evenly across all properties"
              className={cn('h-7 px-2 text-xs', BUTTON_STYLES[status])}
            >
              <Shuffle className="h-3 w-3 mr-1" aria-hidden="true" />
              Distribute evenly
            </Button>
          )}

        </div>
      </div>

      {result.blockReason && (
        <p className={cn('text-xs font-medium', LABEL_STYLES[status])}>
          {result.blockReason}
        </p>
      )}

      <div
        className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 space-y-1.5"
        aria-label="Minimum reinvestment progress"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" aria-hidden="true" />
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Minimum reinvestment
            </span>
          </div>
          {fmvDisplay !== null ? (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Target: {fmvDisplay}
            </span>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500 italic">
              FMV not yet confirmed
            </span>
          )}
        </div>

        {fmvDisplay !== null && relinquishedFMV != null && relinquishedFMV > 0 ? (
          <>
            <div
              className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
              role="progressbar"
              aria-valuenow={displayedBarPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${reinvestmentPercent ?? 0}% of minimum reinvestment identified`}
            >
              <div
                className={cn('h-full rounded-full transition-all duration-300', REINVESTMENT_BAR_FILL[reinvestmentFillKey])}
                style={{ width: `${displayedBarPercent}%` }}
              />
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              {reinvestmentPercent !== null
                ? `You've identified ${formatDollars(totalAllocated)} of the ${fmvDisplay} minimum to fully defer — ${reinvestmentPercent}%`
                : `Identify properties to see progress toward ${fmvDisplay}`}
            </p>
          </>
        ) : (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Confirm your settlement statement to unlock the reinvestment target.
          </p>
        )}
      </div>
    </div>
  )
}

export { computeCompliance }
